import React, { useEffect, useRef, useState } from "react"
import { useHistory } from "react-router-dom"
import styled from "styled-components"
import Matter from "matter-js"
import { logUserEvent } from "../common/firebase.utils"

const situations = [
  "입사 인사",
  "메일 제목",
  "부고 위로",
  "응원 인사",
  "고객 응대",
  "검토 요청",
  "채용 결과 통보",
  "면접 거절",
  "퇴사 인사",
  "생일 축하",
  "회의 공지",
  "건배사",
  "계절 인사",
  "아침 인사",
  "결혼 축하",
  "명절 인사",
  "퇴사응원",
  "업무요청",
  "새해 인사",
  "연말 인사",
  "감사 인사",
  "업무 보고",
  "자료 전달",
  "월요일 인사",
  "첫인사",
  "재촉 / 리마인더",
  "추석 인사",
  "크리스마스 인사",
  "조문 답례",
  "병문안 위로 인사",
  "담당자 변경 안내",
  "부재 공유",
  "연차 신청 / 근무 조정",
  "회식 안내",
  "미팅 요청",
  "피드백 요청",
  "협업 제안",
  "일정 조율",
  "요청 회신",
  "끝인사",
  "입사 거절",
  "날씨 인사",
  "조의/조문 위로"
]

const PILL_STYLES = [
  {
    bg: "linear-gradient(135deg, #5551FF 0%, #5551FF 100%)",
    border: "rgba(255,255,255,0.15)"
  },
  {
    bg: "linear-gradient(135deg, #0F0E2C 0%, #1a1950 100%)",
    border: "rgba(255,255,255,0.08)"
  },
  {
    bg: "linear-gradient(135deg, #5551FF 0%, #6B52D6 100%)",
    border: "rgba(255,255,255,0.18)"
  },
  {
    bg: "linear-gradient(135deg, #6B52D6 0%, #8356F6 100%)",
    border: "rgba(255,255,255,0.2)"
  },
  {
    bg: "linear-gradient(135deg, #374151 0%, #1F2937 100%)",
    border: "rgba(255,255,255,0.08)"
  },
  {
    bg: "linear-gradient(135deg, #4F46E5 0%, #5551FF 100%)",
    border: "rgba(255,255,255,0.15)"
  },
  {
    bg: "linear-gradient(135deg, #1a1950 0%, #374151 100%)",
    border: "rgba(255,255,255,0.1)"
  }
]

function measureText(text, fontSize) {
  if (typeof document === "undefined") return text.length * fontSize * 0.6
  const canvas = document.createElement("canvas")
  const ctx = canvas.getContext("2d")
  ctx.font = `600 ${fontSize}px Pretendard, sans-serif`
  return ctx.measureText(text).width
}

export default function FallingPills() {
  const history = useHistory()
  const sceneRef = useRef(null)
  const engineRef = useRef(null)
  const [pillElements, setPillElements] = useState([])
  const pillBodiesRef = useRef([])
  const rafRef = useRef(null)
  const [isVisible, setIsVisible] = useState(false)
  const hasSpawned = useRef(false)
  const mouseDownPos = useRef(null)

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasSpawned.current) {
          setIsVisible(true)
          hasSpawned.current = true
        }
      },
      { threshold: 0.3 }
    )
    if (sceneRef.current) observer.observe(sceneRef.current)
    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    if (!isVisible || !sceneRef.current) return

    const container = sceneRef.current
    const width = container.offsetWidth
    const height = container.offsetHeight
    const isMobile = width < 600
    const fontSize = isMobile ? 13 : 15
    const pillHeight = isMobile ? 34 : 40
    const paddingX = isMobile ? 20 : 28

    const { Engine, World, Bodies, Runner } = Matter

    const engine = Engine.create({
      gravity: { x: 0, y: 1.2 }
    })
    engineRef.current = engine

    const wallThickness = 60
    const walls = [
      Bodies.rectangle(
        width / 2,
        height + wallThickness / 2,
        width + 100,
        wallThickness,
        { isStatic: true, friction: 0.8, restitution: 0.2 }
      ),
      Bodies.rectangle(
        -wallThickness / 2,
        height / 2,
        wallThickness,
        height * 2,
        { isStatic: true }
      ),
      Bodies.rectangle(
        width + wallThickness / 2,
        height / 2,
        wallThickness,
        height * 2,
        { isStatic: true }
      )
    ]
    World.add(engine.world, walls)

    const shuffled = [...situations].sort(() => Math.random() - 0.5)
    const pillCount = isMobile ? 20 : 36
    const selected = shuffled.slice(0, pillCount)

    const pillData = selected.map((text, i) => {
      const textWidth = measureText(text, fontSize)
      const pillWidth = textWidth + paddingX * 2
      const x = Math.random() * (width - pillWidth) + pillWidth / 2
      const y = -(i * 55) - Math.random() * 80
      const style = PILL_STYLES[i % PILL_STYLES.length]
      const chamfer = { radius: pillHeight / 2 }

      const body = Bodies.rectangle(x, y, pillWidth, pillHeight, {
        chamfer,
        restitution: 0.3,
        friction: 0.6,
        frictionAir: 0.01,
        density: 0.002,
        angle: (Math.random() - 0.5) * 0.4
      })

      World.add(engine.world, body)

      return { text, body, style, pillWidth, pillHeight }
    })

    pillBodiesRef.current = pillData

    const runner = Runner.create()
    Runner.run(runner, engine)

    const update = () => {
      const newElements = pillData.map(
        ({ text, body, style, pillWidth, pillHeight }) => ({
          text,
          x: body.position.x,
          y: body.position.y,
          angle: body.angle,
          style,
          width: pillWidth,
          height: pillHeight
        })
      )
      setPillElements(newElements)
      rafRef.current = requestAnimationFrame(update)
    }
    rafRef.current = requestAnimationFrame(update)

    const mouse = Matter.Mouse.create(container)
    const mouseConstraint = Matter.MouseConstraint.create(engine, {
      mouse,
      constraint: {
        stiffness: 0.2,
        render: { visible: false }
      }
    })
    // Allow page scroll over the scene
    container.removeEventListener("wheel", mouse.mousewheel)
    World.add(engine.world, mouseConstraint)

    // Click-to-navigate: detect click (not drag) on a pill
    const handleMouseDown = e => {
      const rect = container.getBoundingClientRect()
      const x = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left
      const y = (e.touches ? e.touches[0].clientY : e.clientY) - rect.top
      mouseDownPos.current = { x, y }
    }
    const handleMouseUp = e => {
      if (!mouseDownPos.current) return
      const rect = container.getBoundingClientRect()
      const x =
        (e.changedTouches ? e.changedTouches[0].clientX : e.clientX) - rect.left
      const y =
        (e.changedTouches ? e.changedTouches[0].clientY : e.clientY) - rect.top
      const dx = x - mouseDownPos.current.x
      const dy = y - mouseDownPos.current.y
      const dist = Math.sqrt(dx * dx + dy * dy)
      mouseDownPos.current = null
      if (dist > 5) return // dragged, not clicked

      const clickedPill = pillData.find(({ body }) => {
        return Matter.Bounds.contains(body.bounds, { x, y })
      })
      if (clickedPill) {
        logUserEvent("click_landing_situation", { situation: clickedPill.text })
        history.push(`/templates?situation=${clickedPill.text}`)
      }
    }
    container.addEventListener("mousedown", handleMouseDown)
    container.addEventListener("mouseup", handleMouseUp)
    container.addEventListener("touchstart", handleMouseDown, { passive: true })
    container.addEventListener("touchend", handleMouseUp, { passive: true })

    return () => {
      cancelAnimationFrame(rafRef.current)
      Runner.stop(runner)
      Engine.clear(engine)
      World.clear(engine.world)
      container.removeEventListener("mousedown", handleMouseDown)
      container.removeEventListener("mouseup", handleMouseUp)
      container.removeEventListener("touchstart", handleMouseDown)
      container.removeEventListener("touchend", handleMouseUp)
    }
  }, [isVisible])

  return (
    <Section>
      <SectionLabel>900가지+ 상황</SectionLabel>
      <SectionTitle>
        이렇게 <strong>다양한 상황</strong>이 준비되어 있어요
      </SectionTitle>
      <Scene ref={sceneRef}>
        {pillElements.map((pill, i) => (
          <Pill
            key={i}
            style={{
              width: pill.width,
              height: pill.height,
              transform: `translate(${pill.x - pill.width / 2}px, ${
                pill.y - pill.height / 2
              }px) rotate(${pill.angle}rad)`,
              background: pill.style.bg,
              borderColor: pill.style.border
            }}
          >
            {pill.text}
          </Pill>
        ))}
      </Scene>
    </Section>
  )
}

const Section = styled.div`
  padding: 40px 24px 0;
  text-align: center;
  overflow: hidden;

  @media (max-width: 800px) {
    padding: 28px 16px 0;
  }
`

const SectionLabel = styled.p`
  font-size: 14px;
  font-weight: 600;
  color: #5551ff;
  margin-bottom: 0;
  letter-spacing: -0.01em;
`

const SectionTitle = styled.h2`
  font-size: 32px;
  font-weight: 400;
  color: #0f0e2c;
  line-height: 140%;
  margin-bottom: 40px;

  strong {
    font-weight: 700;
  }

  @media (max-width: 800px) {
    font-size: 18px;
    margin-bottom: 28px;
  }
`

const Scene = styled.div`
  position: relative;
  width: 100vw;
  margin-left: calc(-50vw + 50%);
  height: 220px;
  overflow: hidden;
  background: #fafafa;
  cursor: grab;

  &:active {
    cursor: grabbing;
  }

  @media (max-width: 800px) {
    height: 180px;
  }

  @media (max-width: 480px) {
    height: 150px;
  }
`

const Pill = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(255, 255, 255, 0.9);
  font-family: "Pretendard", sans-serif;
  font-size: 15px;
  font-weight: 600;
  border-radius: 100px;
  white-space: nowrap;
  pointer-events: auto;
  user-select: none;
  cursor: pointer;
  will-change: transform;
  border: 1px solid;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2), 0 1px 2px rgba(0, 0, 0, 0.1),
    inset 0 2px 4px rgba(255, 255, 255, 0.25),
    inset 0 -2px 6px rgba(0, 0, 0, 0.15);
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.25);

  &::before {
    content: "";
    position: absolute;
    top: 2px;
    left: 10%;
    right: 10%;
    height: 40%;
    background: linear-gradient(
      180deg,
      rgba(255, 255, 255, 0.3) 0%,
      rgba(255, 255, 255, 0) 100%
    );
    border-radius: 100px;
    pointer-events: none;
  }

  @media (max-width: 600px) {
    font-size: 13px;
  }
`
